import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  FileText, 
  Target,
  Calendar,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Eye
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Lead, Submission, Product } from '@/types/forms';

interface MetricsData {
  leadsByDay: Array<{ date: string; leads: number; submissions: number }>;
  leadsByProduct: Array<{ product: string; leads: number; submissions: number; conversionRate: number }>;
  conversionFunnel: Array<{ stage: string; count: number; percentage: number }>;
  topPages: Array<{ page: string; visits: number; conversionRate: number }>;
  recentActivity: Array<{ date: string; type: string; count: number }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1'];

export default function Metrics() {
  const [metrics, setMetrics] = useState<MetricsData>({
    leadsByDay: [],
    leadsByProduct: [],
    conversionFunnel: [],
    topPages: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      startDate.setDate(endDate.getDate() - days);

      // Load leads and submissions data
      const [leadsResult, submissionsResult, productsResult] = await Promise.all([
        supabase
          .from('leads')
          .select('created_at, recommended_products')
          .gte('created_at', startDate.toISOString()),
        supabase
          .from('submissions')
          .select('submitted_at, products!inner(slug, name)')
          .gte('submitted_at', startDate.toISOString()),
        supabase
          .from('products')
          .select('slug, name, group')
      ]);

      if (leadsResult.error) throw leadsResult.error;
      if (submissionsResult.error) throw submissionsResult.error;
      if (productsResult.error) throw productsResult.error;

      const leads = leadsResult.data || [];
      const submissions = submissionsResult.data || [];
      const products = productsResult.data || [];

      // Generate leads by day
      const leadsByDay = generateLeadsByDay(leads, submissions, days);
      
      // Generate leads by product
      const leadsByProduct = generateLeadsByProduct(leads, submissions, products);
      
      // Generate conversion funnel (mock data for now)
      const conversionFunnel = generateConversionFunnel(leads, submissions);
      
      // Generate top pages (mock data)
      const topPages = generateTopPages();
      
      // Generate recent activity
      const recentActivity = generateRecentActivity(leads, submissions);

      setMetrics({
        leadsByDay,
        leadsByProduct,
        conversionFunnel,
        topPages,
        recentActivity
      });

    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const generateLeadsByDay = (leads: Lead[], submissions: Submission[], days: number) => {
    const data = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayLeads = leads.filter(lead => 
        lead.created_at?.startsWith(dateStr)
      ).length;
      
      const daySubmissions = submissions.filter(sub => 
        sub.submitted_at?.startsWith(dateStr)
      ).length;
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        leads: dayLeads,
        submissions: daySubmissions
      });
    }
    return data;
  };

  const generateLeadsByProduct = (leads: Lead[], submissions: Submission[], products: Product[]) => {
    return products.map(product => {
      const productLeads = leads.filter(lead => 
        lead.recommended_products?.includes(product.slug)
      ).length;
      
      const productSubmissions = submissions.filter(sub => 
        sub.products?.slug === product.slug
      ).length;
      
      const conversionRate = productLeads > 0 ? (productSubmissions / productLeads) * 100 : 0;
      
      return {
        product: product.name,
        leads: productLeads,
        submissions: productSubmissions,
        conversionRate: Math.round(conversionRate * 10) / 10
      };
    }).filter(item => item.leads > 0 || item.submissions > 0);
  };

  const generateConversionFunnel = (leads: Lead[], submissions: Submission[]) => {
    const totalVisitors = Math.max(leads.length * 3, 100); // Estimate visitors
    const totalLeads = leads.length;
    const totalSubmissions = submissions.length;
    const totalCompleted = Math.round(totalSubmissions * 0.8); // Estimate completed
    
    return [
      { stage: 'Visitors', count: totalVisitors, percentage: 100 },
      { stage: 'Leads Generated', count: totalLeads, percentage: Math.round((totalLeads / totalVisitors) * 100) },
      { stage: 'Applications Started', count: totalSubmissions, percentage: Math.round((totalSubmissions / totalVisitors) * 100) },
      { stage: 'Applications Completed', count: totalCompleted, percentage: Math.round((totalCompleted / totalVisitors) * 100) }
    ];
  };

  const generateTopPages = () => {
    return [
      { page: 'Homepage', visits: 1250, conversionRate: 12.5 },
      { page: 'Decision Tree', visits: 890, conversionRate: 28.3 },
      { page: 'Property Insurance', visits: 650, conversionRate: 15.2 },
      { page: 'Liability Insurance', visits: 520, conversionRate: 18.7 },
      { page: 'About Us', visits: 340, conversionRate: 5.2 }
    ];
  };

  const generateRecentActivity = (leads: Lead[], submissions: Submission[]) => {
    const activity = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayLeads = leads.filter(lead => 
        lead.created_at?.startsWith(dateStr)
      ).length;
      
      const daySubmissions = submissions.filter(sub => 
        sub.submitted_at?.startsWith(dateStr)
      ).length;
      
      activity.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        type: 'leads',
        count: dayLeads
      });
      
      activity.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        type: 'submissions',
        count: daySubmissions
      });
    }
    return activity;
  };

  const totalLeads = metrics.leadsByDay.reduce((sum, day) => sum + day.leads, 0);
  const totalSubmissions = metrics.leadsByDay.reduce((sum, day) => sum + day.submissions, 0);
  const conversionRate = totalLeads > 0 ? (totalSubmissions / totalLeads) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics & Metrics</h1>
          <p className="text-gray-600">Business insurance performance insights</p>
        </div>
        <div className="flex items-center gap-2">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <Button onClick={loadMetrics} disabled={loading} size="sm" variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900">{totalLeads}</p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <ArrowUp className="h-3 w-3 mr-1" />
                  +12% vs last period
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Applications</p>
                <p className="text-2xl font-bold text-gray-900">{totalSubmissions}</p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <ArrowUp className="h-3 w-3 mr-1" />
                  +8% vs last period
                </p>
              </div>
              <FileText className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{conversionRate.toFixed(1)}%</p>
                <p className="text-xs text-red-600 flex items-center mt-1">
                  <ArrowDown className="h-3 w-3 mr-1" />
                  -2% vs last period
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Daily Leads</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.leadsByDay.length > 0 ? Math.round(totalLeads / metrics.leadsByDay.length) : 0}
                </p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <ArrowUp className="h-3 w-3 mr-1" />
                  +5% vs last period
                </p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads by Day */}
        <Card>
          <CardHeader>
            <CardTitle>Leads & Applications Over Time</CardTitle>
            <CardDescription>Daily lead generation and application submissions</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={metrics.leadsByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="leads" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Area type="monotone" dataKey="submissions" stackId="1" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card>
          <CardHeader>
            <CardTitle>Conversion Funnel</CardTitle>
            <CardDescription>Customer journey from visitor to completed application</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={metrics.conversionFunnel} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="stage" type="category" width={120} />
                <Tooltip formatter={(value, name) => [value, name === 'count' ? 'Count' : name]} />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads by Product */}
        <Card>
          <CardHeader>
            <CardTitle>Performance by Product</CardTitle>
            <CardDescription>Leads and conversion rates for each insurance product</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={metrics.leadsByProduct}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="product" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="leads" fill="#8884d8" name="Leads" />
                <Bar dataKey="submissions" fill="#82ca9d" name="Applications" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Pages</CardTitle>
            <CardDescription>Most visited pages and their conversion rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {metrics.topPages.map((page, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Eye className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="font-medium">{page.page}</p>
                      <p className="text-sm text-gray-600">{page.visits} visits</p>
                    </div>
                  </div>
                  <Badge variant={page.conversionRate > 15 ? 'default' : 'secondary'}>
                    {page.conversionRate}% conversion
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
